import React, { useState } from "react";
import { Modal, TableRow } from "@mui/material";
import { Visibility as PreviewIcon } from "@mui/icons-material";
import {
    ButtonRow,
    Expandable,
    ModalWrapper,
    StyledBackdrop,
    TitleText,
} from "../../../../MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton } from "../../../../MaterialOrder/Styled";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../../shared/components/TextBox";
import IconButton from "../../../../../shared/components/IconButton";
import DeleteIcon from "../../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../../utils/format";
import { api } from "../../../../../shared/hooks/api";
import { error, success } from "../../../../../utils/toast";
import LoadingButton from "../../../../../shared/components/LoadingButton";
import DocxPreviewModal from "../../../../../shared/components/DocxPreviewModal";
import { generateDocumentBlob } from "../../../../../utils/exportUtils";
import { buildDocxData } from "../../../../../utils/docxBuilder";
import { productImportPreviewDescriptor } from "../../../../../utils/docxMappings";
import ImportTemplate from "../../../../../templates/phieu_nhap_kho_san_pham.docx";
import { ImportProductForm } from "../../../../Products/Title/ImportProductModal/ImportProductForm";

type PrefillItem = {
    id: string;
    name: string;
    importQuantity: number;
    note?: string;
};

interface ImportDeliveryTripModalProps {
    open: boolean;
    handleClose: () => void;
    initialFormData?: any;
    initialItems?: PrefillItem[];
    onImported?: () => void;
    deliveryTripId: string;
}

const TableColumns = [
    { text: "Sản phẩm/vật tư", icon: <></> },
    { text: "Số lượng nhập", icon: <></> },
    { text: "Ghi chú", icon: <></> },
    { text: "", icon: <></> },
];

const ProductRow = ({ item, extraRowProps }: any) => (
    <TableRow>
        <StyledTableCell>{item.name}</StyledTableCell>
        <StyledTableCell>
            <TextBox
                numeric
                style={{ width: 80 }}
                text={formatInternationalNumber(item.importQuantity ?? "")}
                onChangeText={(value: string) => {
                    const n = parseInternationalToNumber(value, error);
                    if (!Number.isFinite(n)) return;
                    extraRowProps.onChangeImportQuantity(item.id, n);
                }}
            />
        </StyledTableCell>
        <StyledTableCell>
            <TextBox
                text={item.note || ""}
                onChangeText={(value: string) => extraRowProps.onChangeNote(item.id, value)}
                style={{ width: 150 }}
            />
        </StyledTableCell>
        <StyledTableCell>
            <IconButton onClick={() => extraRowProps.removeSelectedItem(item)}>
                <DeleteIcon />
            </IconButton>
        </StyledTableCell>
    </TableRow>
);

export const ImportDeliveryTripModal = ({
                                            open,
                                            handleClose,
                                            initialFormData,
                                            initialItems,
                                            onImported,
                                            deliveryTripId,
                                        }: ImportDeliveryTripModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [tableItems, setTableItems] = useState<any[]>(Array.isArray(initialItems) ? initialItems as any[] : []);
    const [formData, setFormData] = useState<any>(initialFormData || {});
    const [{ isCreating }, createDeliveryTripImport] = api.post(`/product-flow/v2/delivery-trips/${deliveryTripId}/import/`);
    const mergeFormData = (patch: any) => setFormData((prev: any) => ({ ...prev, ...patch }));

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    const onChangeImportQuantity = (id: string, quantity: number) => {
        setTableItems(prev => prev.map(p => p.id === id ? { ...p, importQuantity: quantity } : p));
    };
    const onChangeNote = (id: string, note: string) => {
        setTableItems(prev => prev.map(p => p.id === id ? { ...p, note } : p));
    };
    const removeSelectedItem = (removedItem: any) => {
        setTableItems(prev => prev.filter((p: any) => p.id !== removedItem.id));
    };

    const onClickImport = async () => {
        try {
            const response = await createDeliveryTripImport({ delivery_trip_id: deliveryTripId, return_items: tableItems });
            let message = "Nhập kho sản phẩm/vật tư thành công";
            if (response?.imported_storages && Array.isArray(response.imported_storages)) {
                message += `. Kho đã nhập: ${response.imported_storages.join(", ")}`;
            }
            onImported && onImported();
            success("THÀNH CÔNG", message);
            handleCloseWithAnimation();
        } catch (err) {
            error("Nhập kho sản phẩm/vật tư thất bại", err);
        }
    };

    const handlePreview = async () => {
        try {
            // Validate required Customer, Import date
            if (!formData.customer) {
                error("Vui lòng chọn khách hàng");
                return;
            }
            if (!formData.import_date) {
                error("Vui lòng chọn ngày nhập kho");
                return;
            }

            const data = buildDocxData(
                { formData, tableItems },
                productImportPreviewDescriptor,
            );
            const blob = await generateDocumentBlob(data, ImportTemplate);
            setPreviewBlob(blob);
            setIsPreviewOpen(true);
        } catch (e) {
            console.error("Failed to generate product import preview DOCX:", e);
            alert("Không thể tạo bản xem trước tài liệu.");
        }
    };

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

    return (
        <>
            <Modal
                open={open}
                onClose={(_, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
                    handleCloseWithAnimation();
                }}
                closeAfterTransition
                BackdropComponent={StyledBackdrop}
                BackdropProps={{
                    timeout: 500,
                    onClick: (e) => e.stopPropagation(),
                }}
            >
                <ModalWrapper isOpen={isOpen}>
                    <Expandable>
                        {/* Form section (requested for DeliveryTrip modals) */}
                        <ImportProductForm formData={formData} onChange={mergeFormData} />
                        <TitleText>Danh sách nhập kho sản phẩm/vật tư</TitleText>

                        <CustomTable
                            itemList={tableItems}
                            RowElement={ProductRow}
                            TableColumns={TableColumns}
                            extraRowProps={{
                                onChangeImportQuantity,
                                onChangeNote,
                                removeSelectedItem,
                            }}
                            containerStyle={{ maxHeight: "400px" }}
                        />
                    </Expandable>

                    <ButtonRow>
                        <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                        <StyledButton onClick={handlePreview} startIcon={<PreviewIcon />} variant="contained">Xem trước
                            phiếu</StyledButton>
                    </ButtonRow>
                </ModalWrapper>
            </Modal>
            <DocxPreviewModal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                blob={previewBlob}
                title="Xem trước phiếu nhập kho thành phẩm"
                BackdropComponent={StyledBackdrop}
                backdropProps={{ timeout: 300 }}
                debug
                actions={
                    <LoadingButton
                        variant="contained"
                        onClick={onClickImport}
                        disabled={isCreating}
                        isLoading={isCreating}
                        text="Nhập kho"
                    />
                }
            />
        </>
    );
};

export default ImportDeliveryTripModal;
